'use client';

import React from 'react';
import { NextIntlClientProvider } from 'next-intl';
import { defaultLocale } from './locales';
import { useResolvedLocale } from './useResolvedLocale';
import HtmlLangUpdater from '@/components/common/HtmlLangUpdater';

// Statically import messages
import enCommon from '@/messages/en/common.json';
import esCommon from '@/messages/es/common.json';
import ptCommon from '@/messages/pt/common.json';
import zhCommon from '@/messages/zh/common.json';
import hiCommon from '@/messages/hi/common.json';
import deCommon from '@/messages/de/common.json';
import viCommon from '@/messages/vi/common.json';
import frCommon from '@/messages/fr/common.json';
import ruCommon from '@/messages/ru/common.json';
import itCommon from '@/messages/it/common.json';
import jaCommon from '@/messages/ja/common.json';
import enAuth from '@/messages/en/auth.json';
import esAuth from '@/messages/es/auth.json';
import ptAuth from '@/messages/pt/auth.json';
import zhAuth from '@/messages/zh/auth.json';
import hiAuth from '@/messages/hi/auth.json';
import deAuth from '@/messages/de/auth.json';
import viAuth from '@/messages/vi/auth.json';
import frAuth from '@/messages/fr/auth.json';
import ruAuth from '@/messages/ru/auth.json';
import itAuth from '@/messages/it/auth.json';
import jaAuth from '@/messages/ja/auth.json';
import enError from '@/messages/en/error.json';
import esError from '@/messages/es/error.json';
import ptError from '@/messages/pt/error.json';
import zhError from '@/messages/zh/error.json';
import hiError from '@/messages/hi/error.json';
import deError from '@/messages/de/error.json';
import viError from '@/messages/vi/error.json';
import frError from '@/messages/fr/error.json';
import ruError from '@/messages/ru/error.json';
import itError from '@/messages/it/error.json';
import jaError from '@/messages/ja/error.json';
import enPages from '@/messages/en/pages.json';
import esPages from '@/messages/es/pages.json';
import ptPages from '@/messages/pt/pages.json';
import zhPages from '@/messages/zh/pages.json';
import hiPages from '@/messages/hi/pages.json';
import dePages from '@/messages/de/pages.json';
import viPages from '@/messages/vi/pages.json';
import frPages from '@/messages/fr/pages.json';
import ruPages from '@/messages/ru/pages.json';
import itPages from '@/messages/it/pages.json';
import jaPages from '@/messages/ja/pages.json';
const messages: Record<string, any> = {
  en: {
    ...enCommon,
    ...enAuth,
    ...enError,
    ...enPages
  },
  es: {
    ...esCommon,
    ...esAuth,
    ...esError,
    ...esPages
  },
  pt: {
    ...ptCommon,
    ...ptAuth,
    ...ptError,
    ...ptPages
  },
  zh: {
    ...zhCommon,
    ...zhAuth,
    ...zhError,
    ...zhPages
  },
  hi: {
    ...hiCommon,
    ...hiAuth,
    ...hiError,
    ...hiPages
  },
  de: {
    ...deCommon,
    ...deAuth,
    ...deError,
    ...dePages
  },
  vi: {
    ...viCommon,
    ...viAuth,
    ...viError,
    ...viPages
  },
  fr: {
    ...frCommon,
    ...frAuth,
    ...frError,
    ...frPages
  },
  ru: {
    ...ruCommon,
    ...ruAuth,
    ...ruError,
    ...ruPages
  },
  it: {
    ...itCommon,
    ...itAuth,
    ...itError,
    ...itPages
  },
  ja: {
    ...jaCommon,
    ...jaAuth,
    ...jaError,
    ...jaPages
  }
};
export default function I18nProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const locale = useResolvedLocale();
  const currentMessages = messages[locale] ?? messages[defaultLocale];
  return <NextIntlClientProvider locale={locale} messages={currentMessages} onError={error => {
    // Suppress ENVIRONMENT_FALLBACK errors during SSR
    if (error.code === 'ENVIRONMENT_FALLBACK') {
      return;
    }
    console.error(error);
  }} data-sentry-element="NextIntlClientProvider" data-sentry-component="I18nProvider" data-sentry-source-file="I18nProvider.tsx">
      <HtmlLangUpdater data-sentry-element="HtmlLangUpdater" data-sentry-source-file="I18nProvider.tsx" />
      {children}
    </NextIntlClientProvider>;
}